import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { GetSignalStatsResponse, SignalStats } from "./types";
import { getSignalsQueryKeyPrefix } from "./useGetSignals";

import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

const SIGNAL_STATS_CACHE_MS = 60 * 1000;

interface UseGetSignalStatsArgs {
  userToken: string;
  workspaceId: number;
  signalIds: number[];
  enabled?: boolean;
}

export const getSignalStatsQueryKey = ({
  workspaceId,
  signalIds,
}: {
  workspaceId: number;
  signalIds: number[];
}) => [...getSignalsQueryKeyPrefix(workspaceId), "stats", signalIds.join(",")] as const;

export const useGetSignalStats = ({
  userToken,
  workspaceId,
  signalIds,
  enabled = true,
}: UseGetSignalStatsArgs) => {
  const uniqueIds = useMemo(
    () =>
      Array.from(new Set(signalIds.filter(id => Number.isFinite(id) && id > 0))).sort(
        (a, b) => a - b,
      ),
    [signalIds],
  );

  return useQuery({
    queryKey: getSignalStatsQueryKey({ workspaceId, signalIds: uniqueIds }),
    queryFn: async (): Promise<GetSignalStatsResponse> => {
      const response = await fetch(ENDPOINTS.signals.stats(workspaceId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ signal_ids: uniqueIds }),
      });

      return handleResponse(response);
    },
    enabled: enabled && Boolean(userToken) && workspaceId > 0 && uniqueIds.length > 0,
    staleTime: SIGNAL_STATS_CACHE_MS,
  });
};

export const buildSignalStatsById = (
  stats: SignalStats[] | undefined,
): Map<number, SignalStats> => {
  const byId = new Map<number, SignalStats>();
  for (const item of stats ?? []) {
    byId.set(item.signal_id, item);
  }
  return byId;
};
